// src/router/onboardingGuard.js
import { hasCompletedOnboarding } from "@/services/studentProfile.service";
import { getCurrentUser } from "@/services/auth.service";
import { Logger } from "@/utils/logger";
import { isTestModeAllowed } from "@/config/testMode";

/**
 * Navigation guard to handle student onboarding flow
 * Redirects new students to onboarding and prevents completed students from accessing it
 *
 * @param {Object} to - The target route
 * @param {Object} from - The route being navigated from
 * @param {Function} next - The navigation callback
 */
export const onboardingGuard = async (to, from, next) => {
  try {
    // Enhanced Cypress test mode handling (build-gated; never active in production)
    if (isTestModeAllowed()) {
      // In test mode, use mock auth and services
      const mockUser = window.__getAuthState ? window.__getAuthState() : null;

      if (!mockUser) {
        next();
        return;
      }

      // Skip guard for teachers
      if (mockUser.isTeacher) {
        next();
        return;
      }

      // Use mock student profile service if available
      let completed = false;
      if (window.__mockStudentProfileService) {
        try {
          completed =
            await window.__mockStudentProfileService.hasCompletedOnboarding(
              mockUser.uid
            );
        } catch (error) {
          console.error("Error in mock student profile service:", error);
          // Default based on user's onboarding status
          completed = mockUser.onboardingStatus === "COMPLETED";
        }
      } else {
        // Fallback to user's onboarding status
        completed = mockUser.onboardingStatus === "COMPLETED";
      }

      // Apply onboarding logic in test mode
      if (to.name === "StudentOnboarding") {
        if (completed) {
          next({ name: "TeacherList" });
        } else {
          next();
        }
        return;
      }

      if (
        !completed &&
        to.meta?.requiresAuth &&
        !to.meta?.allowWithoutOnboarding
      ) {
        next({
          name: "StudentOnboarding",
          query: { redirect: to.fullPath },
        });
        return;
      }

      next();
      return;
    }

    const currentUser = getCurrentUser();

    // Skip guard if no user (auth guard will handle this)
    if (!currentUser) {
      next();
      return;
    }

    // Skip guard for teachers
    if (currentUser.isTeacher) {
      next();
      return;
    }

    // Check onboarding completion status for students
    const completed = await hasCompletedOnboarding(currentUser.uid);

    Logger.log(
      "debug",
      "Checking onboarding status",
      {
        userId: currentUser.uid,
        completed,
        targetRoute: to.name,
        fromRoute: from.name,
      },
      "OnboardingGuard"
    );

    // If trying to access onboarding page
    if (to.name === "StudentOnboarding") {
      if (completed) {
        // Already completed onboarding, redirect to teachers page
        Logger.log(
          "info",
          "Redirecting completed student away from onboarding",
          { userId: currentUser.uid },
          "OnboardingGuard"
        );
        next({ name: "TeacherList" });
      } else {
        // Allow access to onboarding
        next();
      }
      return;
    }

    // For other routes, check if student needs onboarding
    if (
      !completed &&
      to.meta?.requiresAuth &&
      !to.meta?.allowWithoutOnboarding
    ) {
      // New student trying to access protected route, redirect to onboarding
      Logger.log(
        "info",
        "Redirecting new student to onboarding",
        {
          userId: currentUser.uid,
          targetRoute: to.name,
        },
        "OnboardingGuard"
      );

      next({
        name: "StudentOnboarding",
        query: { redirect: to.fullPath },
      });
      return;
    }

    // All checks passed, proceed with navigation
    next();
  } catch (error) {
    Logger.log(
      "error",
      "Error in onboarding guard",
      {
        error: error.message,
        targetRoute: to.name,
      },
      "OnboardingGuard"
    );

    // On error, let navigation proceed to avoid blocking the app
    next();
  }
};

export default onboardingGuard;
